import React, { useEffect, useRef, useState } from 'react';
import TopBarSpacer from './components/TopBarSpacer';
import BottomNav from './components/BottomNav';
import EventService from '../services/EventService';
import { OfflineCache } from './services/OfflineCache';

function AppShell({ active, children }) {
    const [online, setOnline] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const syncMinVisibleUntilRef = useRef(0);

    useEffect(() => {
        let removeNetworkListener = null;
        let pollInterval = null;
        const setup = async () => {
            const fallbackUpdate = () => setOnline(navigator.onLine);
            fallbackUpdate();
            window.addEventListener('online', fallbackUpdate);
            window.addEventListener('offline', fallbackUpdate);
            // Poll every 3s as a last resort
            pollInterval = setInterval(() => {
                setOnline(navigator.onLine);
            }, 3000);

            try {
                const mod = await import('@capacitor/network');
                const status = await mod.Network.getStatus();
                setOnline(status.connected);
                const listener = await mod.Network.addListener('networkStatusChange', async (e) => {
                    setOnline(e.connected);
                    if (e.connected) {
                        // Try to sync all pending scans per event
                        const pending = OfflineCache.getAllPendingScans();
                        if (pending.length) {
                            setSyncing(true);
                            syncMinVisibleUntilRef.current = Date.now() + 5000; // keep banner at least 5s
                            // group by eventId
                            const grouped = pending.reduce((acc, p) => {
                                acc[p.eventId] = acc[p.eventId] || [];
                                acc[p.eventId].push({ ticketId: p.ticketId, scanned: !!p.scanned });
                                return acc;
                            }, {});
                            for (const eventId of Object.keys(grouped)) {
                                try {
                                    // dedupe per event
                                    const map = new Map();
                                    for (const t of grouped[eventId]) {
                                        const prev = map.get(t.ticketId);
                                        map.set(t.ticketId, prev ? { ticketId: t.ticketId, scanned: prev.scanned || t.scanned } : t);
                                    }
                                    const payload = Array.from(map.values());
                                    if (payload.length) {
                                        await EventService.submitChangedTickets(payload);
                                        OfflineCache.clearPendingScansForEvent(eventId);
                                        // Optionally refresh cache for the event
                                        try {
                                            const res = await EventService.getEventCheckIn(eventId);
                                            OfflineCache.saveEventTickets(eventId, res.data.tickets || []);
                                        } catch (_) {}
                                    }
                                } catch (_) {
                                    // leave pending; will retry next time
                                }
                            }
                            const remaining = syncMinVisibleUntilRef.current - Date.now();
                            if (remaining > 0) {
                                setTimeout(() => setSyncing(false), remaining);
                            } else {
                                setSyncing(false);
                            }
                        }
                    }
                });
                removeNetworkListener = () => listener.remove();
            } catch (_) {
                // plugin not available; rely on navigator.onLine
            }

            return () => {
                window.removeEventListener('online', fallbackUpdate);
                window.removeEventListener('offline', fallbackUpdate);
            };
        };
        setup();
        return () => {
            if (removeNetworkListener) removeNetworkListener();
            if (pollInterval) clearInterval(pollInterval);
        };
    }, []);
    return (
        <div className="min-h-screen bg-white">
            <TopBarSpacer />
            <div className="pb-20 relative">
                {children}
            </div>
            {!online && (
                <div className="fixed inset-x-0 bottom-16 z-50 px-4 pb-2 pointer-events-none">
                    <div className="bg-yellow-100 border border-yellow-300 text-yellow-900 text-sm rounded-md px-3 py-1.5 shadow text-center">
                        Not connected to the internet
                    </div>
                </div>
            )}
            {online && syncing && (
                <div className="fixed inset-x-0 bottom-16 z-50 px-4 pb-2 pointer-events-none">
                    <div className="bg-blue-100 border border-blue-300 text-blue-900 text-sm rounded-md px-3 py-1.5 shadow text-center">
                        Back online — syncing data
                    </div>
                </div>
            )}
            <BottomNav active={active} />
        </div>
    );
}

export default AppShell;


